/**
 * Core HTTP Client - Fixed
 * Properly handles backend response format (no success wrapper)
 */

export interface RequestConfig extends RequestInit {
  params?: Record<string, string | number>;
}

export interface ApiResponse<T = unknown> {
  data: T;
  message?: string;
  error?: string;
}

class ApiClient {
  private baseUrl: string;
  private authToken: string | null = null;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_URL || 'https://edge-commerce-api.sv9.workers.dev';

    const tokens = localStorage.getItem('auth_tokens');
    if (tokens) {
      try {
        const { accessToken } = JSON.parse(tokens);
        this.authToken = accessToken;
      } catch (e) {
        console.error('Failed to parse auth tokens:', e);
      }
    }
  }

  setAuthToken(token: string | null) {
    this.authToken = token;
  }

  private async request<T>(
    endpoint: string,
    config: RequestConfig = {}
  ): Promise<T> {
    const url = new URL(endpoint, this.baseUrl);

    if (config.params) {
      Object.entries(config.params).forEach(([key, value]) => {
        url.searchParams.append(key, String(value));
      });
    }

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...config.headers,
    };

    if (this.authToken) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.authToken}`;
    }

    try {
      const response = await fetch(url.toString(), {
        ...config,
        headers,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Request failed');
      }

      return data as T;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Unknown error occurred');
    }
  }

  async get<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'GET' });
  }

  async post<T>(
    endpoint: string,
    body?: unknown,
    config?: RequestConfig
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(
    endpoint: string,
    body?: unknown,
    config?: RequestConfig
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'DELETE' });
  }

  async getDashboardMetrics(): Promise<any> {
    return this.get<any>('/dashboard/metrics');
  }

  async getRevolutionaryMetrics(): Promise<any> {
    return this.get<any>('/dashboard/revolutionary-metrics');
  }

  async createPayment(data: any): Promise<any> {
    return this.post<any>('/payments', data);
  }

  async getTenants(): Promise<any> {
    return this.get<any>('/tenants');
  }

  async regenerateTenant(tenantId: string): Promise<any> {
    return this.post<any>(`/tenants/${tenantId}/regenerate`);
  }
}

// Create singleton instance
export const api = new ApiClient();

export default ApiClient;
